/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import * as _ from 'lodash';

/**
 * FcAgCalendarComponent.
 */
@Component({
    selector: 'fc-ag-calendar',
    templateUrl: './fc-ag-calendar.component.html',
    styleUrls: ['./fc-ag-calendar.component.scss']
})
export class FcAgCalendarComponent implements OnInit {

    /**
     * ViewChild ElementRef DateInput.
     */
    @ViewChild('dateInput') dateInput: ElementRef;

    private _selectedDate: any;
    
    private _disabled: any;

    /**
     * Getter Selected Date.
     *
     * @returns Date.
     */
    get selectedDate(): any {
        return this._selectedDate;
    }

    /**
     * Setter Selected Date.
     *
     * @param value Date.
     */
    @Input() set selectedDate(value: any) {
        this._selectedDate = value;
        this.initCalendarData();
    }

    /**
     * Date generator.
     * 
     * @param year Year value.
     * @param month Month value.
     * @param day Day value.
     * @returns Date.
     */
    @Input()
    dateGenerator: Function = function(year: number, month:  number, day: number): Date {
        return new Date(year, month, day);
    }

    /**
     * The maximum valid date.
     */
    @Input() maxDate!: Date;

    /**
     * The minimum valid date.
     */
    @Input() minDate!: Date;
    
    /**
     * Allow select month and year.
     */
    @Input() allowSelectMonthYear: boolean = false;

    /**
     * Setter Disable select date in calendar.
     * 
     * @param value Disable state. 
     */
    @Input() set disabled(value: boolean) {
        this._disabled = value;
        this.populateDays();
    }

    /**
     * Getter disable Date.
     *
     * @returns Date.
     */
    get disabled(): any {
        return this._disabled;
    }

    /**
     * Event Emitter of Date Select.
     */
    @Output() dateSelection: EventEmitter<any>;

    /**
     * Month of years array.
     */
    readonly MONTH_NAMES = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    /**
     * Years array.
     */
    readonly YEARS = Array.from({ length: 201 }, (_, i) => 1923 + i);

    /**
     * Days of week.
     */
    readonly DAYS: any = Array('S', 'M', 'T', 'W', 'T', 'F', 'S');

    /**
     * Date Picker Value.
     */
    datepickerValue: any = undefined;

    /**
     * Current Month.
     */
    month: number = 0;

    /**
     * Current Year.
     */
    year: number = 0;

    /**
     * All Available/NotAvailable days in month.
     */
    displayDays: any = Array(0);

    /**
     * Blank days before the first days of month.
     */
    blankDays: any = Array(0);

    /**
     * Rest days before the first days of month.
     */
    restDays: any = Array(0);

    /**
     * Month selector backdrop.
     */
    monthSelectorBackdrop = false;

    /**
     * Year selector backdrop.
     */
    yearSelectorBackdrop = false;

    /**
     * Component constructor.
     */
    constructor() {
        this.dateSelection = new EventEmitter<any>();
    }

    /**
     * Angular OnInit implementation.
     */
    ngOnInit() {
        this.initCalendarData();
    }

    /**
     * Init calendar data.
     */
    initCalendarData() {
        let initDate = _.isNil(this.selectedDate) ? new Date() : this.selectedDate;
        this.month = initDate.getMonth();
        this.year = initDate.getFullYear();
        this.datepickerValue = new Date(
            this.year,
            this.month,
            initDate.getDate()
        ).toDateString();
        // Set only the Date ignoring the time.
        if (this.minDate) {
            const minDateString = this.minDate.toDateString();
            this.minDate = new Date(minDateString);
        }
        if (this.maxDate) {
            const maxDateString = this.maxDate.toDateString();
            this.maxDate = new Date(maxDateString);
        }
        this.populateDays();
    }

    /**
     * Determine the date is today.
     *
     * @param date Any.
     * @returns Any.
     */
    isToday(date: any): any {
        const today = new Date();
        const d = new Date(this.year, this.month, date);

        return this.isTwoDateEqual(today, d);
    }

    /**
     * Determine the date is greater than today.
     *
     * @param date Any.
     * @returns Boolean.
     */
    greaterThanToday(date: any): boolean {
        const today = new Date();
        const d = new Date(this.year, this.month, date);
        return d > today;
    }

    /**
     * Determine the date is selected date.
     *
     * @param _sd Any.
     * @returns Boolean.
     */
    isSelectedDate(_sd: any): boolean {
        let slotDate = new Date(this.year, this.month, _sd);
        return this.isTwoDateEqual(this.selectedDate, slotDate);
    }

    /**
     * To get the date value as dd/mm/yyyy.
     *
     * @param date Any.
     */
    getDateValue(date: any) {
        //When filter to date range if mean 23 hours 59 minutes 59 seconds of each day.
        let selectedDate = this.dateGenerator(this.year, this.month, date);
        this.datepickerValue = selectedDate.toDateString();

        this.dateInput.nativeElement.value =
            selectedDate.getFullYear() +
            '-' +
            ('0' + selectedDate.getMonth()).slice(-2) +
            '-' +
            ('0' + selectedDate.getDate()).slice(-2);

        this.dateSelection.emit(selectedDate);
    }

    /**
     * To Populate date in selected month.
     */
    populateDays(): void {
        let daysInMonth = new Date(this.year, this.month + 1, 0).getDate(),
            // find where to start calendar day of week
            dayOfWeek = new Date(this.year, this.month).getDay(),
            blankDayArray = [],
            daysArray = [];

        for (let i = 1; i <= dayOfWeek; i++) {
            blankDayArray.push(i);
        }

        for (let i = 1; i <= daysInMonth; i++) {
            if (this.disabled) {
                daysArray.push({date: i, disabled: true});
            } else {
                const date = new Date(this.year, this.month, i);
                if (this.minDate && this.maxDate) {
                    if (date >= this.minDate && date <= this.maxDate) {
                        daysArray.push({date: i});
                    } else {
                        daysArray.push({date: i, disabled: true});
                    }
                } else if (this.minDate) {
                    if (date >= this.minDate) {
                        daysArray.push({date: i});
                    } else {
                        daysArray.push({date: i, disabled: true});
                    }
                } else if (this.maxDate) {
                    if (date <= this.maxDate) {
                        daysArray.push({date: i});
                    } else {
                        daysArray.push({date: i, disabled: true});
                    }
                } else {
                    daysArray.push({date: i});
                }
            }
        }

        this.blankDays = blankDayArray;
        this.displayDays = daysArray;
        this.restDays = Array(this.DAYS.length
            - (blankDayArray.length + daysArray.length) % this.DAYS.length);
    }


    /**
     * To decrease month.
     */
    decreaseMon(): void {
        if (this.month === 0) {
            this.month = 11;
            this.year--;
        } else {
            this.month--;
        }
    }

    /**
     * To increase month.
     */
    increaseMon(): void {
        if (this.month === 11) {
            this.month = 0;
            this.year++;
        } else {
            this.month++;
        }
    }

    /**
     * Prevent from using toDateString with performance issue.
     *
     * @param date1 Date1.
     * @param date2 Date2.
     * @returns Boolean.
     */
    isTwoDateEqual(date1: Date, date2: Date): boolean {
        if (date1 && date2) {
            return date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth() &&
                date1.getFullYear() === date2.getFullYear();   
        }
        return false;
    }

    /**
     * Open month selector panel.
     */
    openMonthSelector() {
        this.monthSelectorBackdrop = !this.monthSelectorBackdrop;
        setTimeout(() => {
            const selectedElm = document.querySelector('.calendar-dropdown-item.selected');
            selectedElm?.scrollIntoView({block: 'nearest'});
        });
    }

    /**
     * Open year selector panel.
     */
    openYearSelector() {
        this.yearSelectorBackdrop = !this.yearSelectorBackdrop;
        setTimeout(() => {
            const selectedElm = document.querySelector('.calendar-dropdown-item.selected');
            selectedElm?.scrollIntoView({block: 'nearest'});
        });
    }
}
